import React, { useRef, useState, useEffect } from "react"
import { SecondaryHistoryType } from "../../types/mainModulTypes"
import { API } from "../../constants/constants"
import { Navigation, Pagination } from "swiper/modules"
import { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import BaseHeadline from "../common/BaseHeadline"
import BaseSubheadline from "../common/BaseSubheadline"
import BaseRichtext from "../common/BaseRichtext"

export default function SecondaryHistory(props: SecondaryHistoryType) {
    const swiperRef = useRef<SwiperType>()

    const [isBeginning, setIsBeginning] = useState(true)
    const [isEnd, setIsEnd] = useState(false)

    useEffect(() => {
        if (swiperRef.current) {
            setIsBeginning(swiperRef.current.isBeginning)
            setIsEnd(swiperRef.current.isEnd)
        }
    }, [])

    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 640)
        checkMobile()
        window.addEventListener("resize", checkMobile)
        return () => window.removeEventListener("resize", checkMobile)
    }, [])

    const modules = isMobile ? [Pagination] : [Navigation]

    const mobileImages = props?.secondaryHistoryGalleryBlock?.flatMap((item) => [
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine1Image1?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine1Image1?.description || item.secondaryHistoryGalleryBlockEntryLine1Image1?.name,
        },
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine1Image2?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine1Image2?.description || item.secondaryHistoryGalleryBlockEntryLine1Image2?.name,
        },
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine2Image?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine2Image?.description || item.secondaryHistoryGalleryBlockEntryLine2Image?.name,
        },
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine3Image1?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine3Image1?.description || item.secondaryHistoryGalleryBlockEntryLine3Image1?.name,
        },
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine3Image2?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine3Image2?.description || item.secondaryHistoryGalleryBlockEntryLine3Image2?.name,
        },
        {
            src: API + item.secondaryHistoryGalleryBlockEntryLine3Image3?.url,
            alt: item.secondaryHistoryGalleryBlockEntryLine3Image3?.description || item.secondaryHistoryGalleryBlockEntryLine3Image3?.name,
        },
    ])

    return (
        <div className="max-w-content mx-auto px-4 lg:px-0">
            <div className="flex flex-col sm:flex-row gap-12 sm:gap-4 pb-8 sm:pb-16">
                <div className="w-full sm:w-[calc(50%-0.5rem)] flex flex-col gap-8">
                    <div>
                        <BaseHeadline headlineType={3} label={props.secondaryHistoryLeftHeading} />
                        <BaseSubheadline label={props.secondaryHistoryLeftSubHeading} classProps="pb-1" />
                        <BaseRichtext content={props.secondaryHistoryLeftText} />
                    </div>
                    <div className="flex-grow flex flex-col gap-4 justify-evenly items-center">
                        <img
                            src={API + props?.secondaryHistoryLeftImage1?.url}
                            alt={props.secondaryHistoryLeftImage1?.description ?? props.secondaryHistoryLeftImage1?.name}
                            className="w-[205px]"
                        />
                        <img
                            src={API + props?.secondaryHistoryLeftImage2?.url}
                            alt={props.secondaryHistoryLeftImage2?.description ?? props?.secondaryHistoryLeftImage2?.name}
                            className="w-[205px]"
                        />
                    </div>
                </div>
                <div className="sm:w-[calc(50%-0.5rem)] flex flex-col gap-6 border-l-8 border-primary pl-8">
                    {props?.secondaryHistoryRightBlock?.map((item, index) => (
                        <BaseRichtext
                            key={index}
                            content={
                                "<strong>" + item.secondaryHistoryRightBlockEntryYear + "</strong>" + " " + item.secondaryHistoryRightBlockEntryText
                            }
                        />
                    ))}
                </div>
            </div>

            <div className="flex gap-4 justify-between relative items-center">
                <button
                    onClick={() => {
                        swiperRef.current?.slidePrev()
                        setIsBeginning(swiperRef.current?.isBeginning ?? true)
                        setIsEnd(swiperRef.current?.isEnd ?? false)
                    }}
                    disabled={isBeginning}
                    className={`w-0 h-0 border-t-[50px] border-t-transparent border-b-[50px] border-b-transparent border-l-[30px] sm:block hidden
                ${isBeginning ? "border-l-gray-400 cursor-not-allowed" : "border-l-base-300"} rotate-180`}
                />
                <Swiper
                    slidesPerView={1}
                    spaceBetween={50}
                    modules={[Navigation, Pagination]}
                    onBeforeInit={(swiper: SwiperType) => {
                        swiperRef.current = swiper
                    }}
                    pagination={isMobile ? true : false}
                    className="mySwiper  max-w-content mx-auto">
                    {isMobile
                        ? mobileImages.map((image, idx) => (
                              <SwiperSlide key={idx} className="relative w-full">
                                  <img src={image.src} alt={image.alt} className="w-full aspect-[4/3] object-cover rounded-default" />
                              </SwiperSlide>
                          ))
                        : props?.secondaryHistoryGalleryBlock?.map((item, index) => (
                              <SwiperSlide key={index} className="relative w-full max-w-full">
                                  <div className="grid grid-cols-3 gap-8">
                                      <img
                                          src={API + item.secondaryHistoryGalleryBlockEntryLine1Image1?.url}
                                          alt={
                                              item.secondaryHistoryGalleryBlockEntryLine1Image1?.description ||
                                              item.secondaryHistoryGalleryBlockEntryLine1Image1?.name
                                          }
                                          className="col-span-2 w-full h-44 object-cover rounded-default"
                                      />
                                      <img
                                          src={API + item.secondaryHistoryGalleryBlockEntryLine1Image2?.url}
                                          alt={
                                              item.secondaryHistoryGalleryBlockEntryLine1Image2?.description ||
                                              item.secondaryHistoryGalleryBlockEntryLine1Image2?.name
                                          }
                                          className="col-span-1 w-full h-44 object-cover rounded-default"
                                      />
                                      <img
                                          src={API + item.secondaryHistoryGalleryBlockEntryLine2Image?.url}
                                          alt={
                                              item.secondaryHistoryGalleryBlockEntryLine2Image?.description ||
                                              item.secondaryHistoryGalleryBlockEntryLine2Image?.name
                                          }
                                          className="col-span-3 w-full h-[22rem] object-cover rounded-default"
                                      />
                                      <img
                                          src={API + item.secondaryHistoryGalleryBlockEntryLine3Image1?.url}
                                          alt={
                                              item.secondaryHistoryGalleryBlockEntryLine3Image1?.description ||
                                              item.secondaryHistoryGalleryBlockEntryLine3Image1?.name
                                          }
                                          className="col-span-2 w-full h-[22rem] object-cover rounded-default"
                                      />
                                      <div className="col-span-1 flex flex-col gap-8">
                                          <img
                                              src={API + item.secondaryHistoryGalleryBlockEntryLine3Image2?.url}
                                              alt={
                                                  item.secondaryHistoryGalleryBlockEntryLine3Image2?.description ||
                                                  item.secondaryHistoryGalleryBlockEntryLine3Image2?.name
                                              }
                                              className="w-full h-40 object-cover rounded-default"
                                          />
                                          <img
                                              src={API + item.secondaryHistoryGalleryBlockEntryLine3Image3?.url}
                                              alt={
                                                  item.secondaryHistoryGalleryBlockEntryLine3Image3?.description ||
                                                  item.secondaryHistoryGalleryBlockEntryLine3Image3?.name
                                              }
                                              className="w-full h-40 object-cover rounded-default"
                                          />
                                      </div>
                                  </div>
                              </SwiperSlide>
                          ))}
                </Swiper>

                <button
                    onClick={() => {
                        swiperRef.current?.slideNext()
                        setIsBeginning(swiperRef.current?.isBeginning ?? true)
                        setIsEnd(swiperRef.current?.isEnd ?? false)
                    }}
                    disabled={isEnd}
                    className={`w-0 h-0 border-t-[50px] border-t-transparent border-b-[50px] border-b-transparent border-l-[30px] sm:block hidden
                ${isEnd ? "border-l-gray-400 cursor-not-allowed" : "border-l-base-300"}`}
                />
            </div>
        </div>
    )
}
